import { buttonVariants } from "@nt/ui/recipes";
import { ExternalLink } from "lucide-react";
import { Link } from "react-router";

import { Section } from "@/components/section";
import { shortDate } from "@/lib/format";
import { useGet } from "@/lib/queries";
import type { ProgramData } from "@/lib/types";
import { TERMS_URL } from "@/pages/resources";

/** The fee table and the payout rules live on the Rates page; this tab points there. */
export function RatesPointer() {
  return (
    <Section
      title="Your rates"
      description="Your commission schedule, the attribution window, the hold and the payout rules are on the Rates page."
    >
      <Link
        to="/rates"
        className={buttonVariants({ variant: "secondary", className: "w-fit" })}
      >
        See your commission schedule
      </Link>
    </Section>
  );
}

/**
 * The accepted version and the link to the full terms. Shown while the
 * program loads or fails too (with the public terms address), so the terms
 * are always one click away.
 */
export function TermsLink({ program }: { program: ProgramData | undefined }) {
  return (
    <Section
      title="Program terms"
      description={
        program
          ? program.termsAcceptedAt
            ? `You accepted version ${program.termsVersion} on ${shortDate(program.termsAcceptedAt)}.`
            : `Version ${program.termsVersion} applies to your account.`
          : "The rules for fees, the attribution window, holds and payouts."
      }
    >
      <div className="grid gap-4 text-sm">
        <a
          className={buttonVariants({
            variant: "secondary",
            className: "w-fit",
          })}
          href={program?.termsUrl || TERMS_URL}
          target="_blank"
          rel="noreferrer"
        >
          Read the full partner terms
          <ExternalLink aria-hidden />
        </a>
        <p className="text-muted-foreground">
          To leave the program, write to support@nonresident.tax.
        </p>
      </div>
    </Section>
  );
}

/**
 * The Settings › Program terms tab: the accepted version and the full terms
 * (from `GET /api/partner/program`, shared with the Rates page), and a pointer
 * to the Rates page for the fees and rules.
 */
export function TermsTab() {
  const query = useGet<ProgramData>("program", "/api/partner/program");
  return (
    <>
      <TermsLink program={query.data} />
      <RatesPointer />
    </>
  );
}
